import { useSyncExternalStore } from 'react';

export type AppLang = 'en' | 'ms' | 'id';
export type VoiceLang = 'ms-MY' | 'en-US' | 'id-ID' | 'zh-CN' | 'ta-IN';
export type SfxName = 'coin' | 'bell' | 'pop' | 'whoosh' | 'tick' | 'off';

export interface Settings {
  appLang: AppLang;
  voiceLang: VoiceLang;
  /** A specific device voice picked by the user, or null for "best voice for voiceLang". */
  voiceURI: string | null;
  rate: number;
  /** Set once the user moves the speed slider, so default changes do not override their choice. */
  rateChosen?: boolean;
  pitch: number;
  volume: number;
  detectLanguage: boolean;
  voiceOn: boolean;
  sfxOn: boolean;
  read: { chat: boolean; gift: boolean; follow: boolean; share: boolean; join: boolean; like: boolean };
  format: string;
  sfx: { gift: SfxName; follow: SfxName; share: SfxName; chat: SfxName };
  filters: { skipEmojiOnly: boolean; skipLinks: boolean; dedupe: boolean; maxLength: number; blocklist: string; prefix: string };
  queueMax: number;
  /** Read slightly faster when comments pile up. Off unless the user turns it on. */
  speedUpWhenBusy: boolean;
  /** Set once the user flips the speed-up switch, so default changes do not override their choice. */
  speedUpChosen?: boolean;
  /** Use the ElevenLabs voice when this streamer has access to it. */
  premiumVoice: boolean;
  fontSize: number;
  readerSize: number;
}

export const DEFAULT_FORMAT: Record<AppLang, string> = {
  en: '{comment}',
  ms: '{comment}',
  id: '{comment}',
};

/** Earlier defaults; a stored copy of one is migrated to the current default. */
const OLD_DEFAULT_FORMATS = ['{nickname} says {comment}', '{nickname} cakap {comment}'];

const browserPrefs = (): string[] => {
  if (typeof navigator === 'undefined') return [];
  const list = navigator.languages?.length ? navigator.languages : [navigator.language];
  return list.map((l) => (l ?? '').toLowerCase());
};

/**
 * UI language from the browser's preferred languages (first match wins). BacaChat is
 * Indonesia-first, so anything we do not translate falls back to Bahasa Indonesia.
 */
const browserLang = (): AppLang => {
  for (const l of browserPrefs()) {
    const code = l.split(/[-_]/)[0];
    if (code === 'id' || code === 'in') return 'id';
    if (code === 'ms') return 'ms';
    if (code === 'en') return 'en';
  }
  return 'id';
};

/**
 * Voice to start with. Chat on BacaChat is mostly Indonesian or Malay even when the browser is
 * in English, so an English UI still gets an Indonesian voice unless the browser hints Malaysia.
 */
const defaultVoiceLang = (appLang: AppLang): VoiceLang => {
  if (appLang === 'ms') return 'ms-MY';
  if (appLang === 'id') return 'id-ID';
  const prefs = browserPrefs();
  if (prefs.some((l) => l.startsWith('ms') || l.endsWith('-my'))) return 'ms-MY';
  if (prefs.some((l) => l.startsWith('id') || l.endsWith('-id'))) return 'id-ID';
  return 'id-ID';
};

export const defaultSettings = (): Settings => {
  const appLang = browserLang();
  return {
    appLang,
    voiceLang: defaultVoiceLang(appLang),
    voiceURI: null,
    rate: 1,
    pitch: 1,
    volume: 0.8,
    detectLanguage: true,
    voiceOn: true,
    sfxOn: true,
    read: { chat: true, gift: true, follow: true, share: false, join: false, like: false },
    format: DEFAULT_FORMAT[appLang],
    sfx: { gift: 'coin', follow: 'pop', share: 'whoosh', chat: 'off' },
    filters: { skipEmojiOnly: true, skipLinks: true, dedupe: true, maxLength: 150, blocklist: '', prefix: '' },
    queueMax: 10,
    speedUpWhenBusy: false,
    premiumVoice: true,
    fontSize: 18,
    readerSize: 72,
  };
};

const KEY = 'bacachat.settings.v1';
const RECENT_KEY = 'bacachat.recent.v1';

function load(): Settings {
  const d = defaultSettings();
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return d;
    const s = JSON.parse(raw) as Partial<Settings>;
    if (s.format && OLD_DEFAULT_FORMATS.includes(s.format)) s.format = d.format;
    // 1.1 was the old default speed; people who never touched the slider move to the new 1.0.
    if (s.rate === 1.1 && !s.rateChosen) s.rate = d.rate;
    // Speed-up used to default to on; only keep it on for people who switched it on themselves.
    if (!s.speedUpChosen) s.speedUpWhenBusy = d.speedUpWhenBusy;
    return {
      ...d,
      ...s,
      read: { ...d.read, ...(s.read ?? {}) },
      sfx: { ...d.sfx, ...(s.sfx ?? {}) },
      filters: { ...d.filters, ...(s.filters ?? {}) },
    };
  } catch {
    return d;
  }
}

let current: Settings = load();
const listeners = new Set<() => void>();

export function getSettings(): Settings {
  return current;
}

export function updateSettings(patch: Partial<Settings> | ((s: Settings) => Partial<Settings>)): void {
  const p = typeof patch === 'function' ? patch(current) : patch;
  current = { ...current, ...p };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* storage unavailable: settings live for this tab only */
  }
  listeners.forEach((l) => l());
}

export function subscribeSettings(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useSettings(): Settings {
  return useSyncExternalStore(subscribeSettings, getSettings, getSettings);
}

export function getRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, 6) : [];
  } catch {
    return [];
  }
}

export function addRecent(username: string): void {
  try {
    const next = [username, ...getRecent().filter((u) => u !== username)].slice(0, 6);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}
